import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAppContext } from "src/contexts/AppContext";
import { useAuth } from "src/contexts/AuthContext";
import recordCoachInboxVisit from "src/features/communications/services/recordCoachInboxVisit";

const STORAGE_KEY = "coachInboxPendingVisit";

/** Preserve the campaign's case through sign-in, then let the server validate
 * ownership and a real recent send. Visiting a link is not a product action. */
export function useCoachInboxAttribution(): void {
  const { search, pathname } = useLocation();
  const { isLoggedIn } = useAuth();
  const { clients } = useAppContext();
  const inFlight = useRef<string | null>(null);
  useEffect(() => {
    try {
      const params = new URLSearchParams(search);
      const caseId = params.get("utm_content");
      if (
        params.get("utm_source") === "coach_inbox" &&
        caseId &&
        /^[\w-]{1,128}$/.test(caseId)
      ) {
        sessionStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ caseId, at: Date.now() })
        );
      }
      if (!isLoggedIn) return;
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (!stored || inFlight.current === stored) return;
      const pending = JSON.parse(stored) as { caseId: string; at: number };
      if (Date.now() - pending.at > 7 * 86_400_000) {
        sessionStorage.removeItem(STORAGE_KEY);
        return;
      }
      inFlight.current = stored;
      recordCoachInboxVisit({ caseId: pending.caseId }, clients)
        .then(() => {
          if (sessionStorage.getItem(STORAGE_KEY) === stored)
            sessionStorage.removeItem(STORAGE_KEY);
        })
        .catch(() => {
          /* Analytics only: keep the visit for the next authenticated navigation. */
        })
        .finally(() => {
          inFlight.current = null;
        });
    } catch {
      /* Storage may be unavailable; analytics never blocks the app. */
    }
  }, [search, pathname, isLoggedIn, clients]);
}
